package com.vizio.redwolf.auth.api

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.js.JsExport

/**
 * Refer - https://vizio-sc.atlassian.net/l/c/6fQyBrbe
 */
@JsExport
sealed class HasPinResponse {
    @Serializable
    class BadPayload : HasPinResponse()

    @Serializable
    @SerialName("PIN_FOUND")
    data class PinFound(
        val description: String,
        val isPinConfigured: Boolean,
        val isPinLocked: Boolean,
        val attemptsFailed: Int,
        val attemptsRemaining: Int
    ) : HasPinResponse()

    @Serializable
    @SerialName("PIN_NOT_FOUND")
    data class PinNotFound(val description: String) : HasPinResponse()

    @Serializable
    @SerialName("USER_ID_NOT_FOUND")
    data class UserIdNotFound(val description: String) : HasPinResponse()

    @Serializable
    @SerialName("INVALID_BASIC_AUTH_CREDENTIALS")
    data class InvalidBasicAuthCredentials(val description: String) : HasPinResponse()

    @Serializable
    @SerialName("FAILURE")
    data class Failure(val description: String) : HasPinResponse()

    @Serializable
    data class Unauthorized(val message: String): HasPinResponse()

    data class Error(val throwable: Throwable) : HasPinResponse()

    data class Timeout(val throwable: Throwable) : HasPinResponse()

    fun <R> fold (
        onBadPayload: (BadPayload: BadPayload) -> R,
        onPinFound: (PinFound: PinFound) -> R,
        onPinNotFound: (PinNotFound: PinNotFound) -> R,
        onUserIdNotFound: (UserIdNotFound: UserIdNotFound) -> R,
        onInvalidBasicAuthCredentials: (InvalidBasicAuthCredentials: InvalidBasicAuthCredentials) -> R,
        onFailure: (Failure: Failure) -> R,
        onUnauthorized: (Unauthorized: Unauthorized) -> R,
        onError: (Error: Error) -> R,
        onTimeout: (Timeout: Timeout) -> R
    ) : R {
        return when (this) {
            is BadPayload -> onBadPayload(BadPayload())
            is PinFound -> onPinFound(PinFound(this.description, this.isPinConfigured, this.isPinLocked, this.attemptsFailed, this.attemptsRemaining))
            is PinNotFound -> onPinNotFound(PinNotFound(this.description))
            is UserIdNotFound -> onUserIdNotFound(UserIdNotFound(this.description))
            is InvalidBasicAuthCredentials -> onInvalidBasicAuthCredentials(InvalidBasicAuthCredentials(this.description))
            is Failure -> onFailure(Failure(this.description))
            is Unauthorized -> onUnauthorized(Unauthorized(this.message))
            is Error -> onError(Error(this.throwable))
            is Timeout -> onTimeout(Timeout(this.throwable))
        }
    }
}

@JsExport
sealed class CreatePinResponse {
    @Serializable
    class BadPayload : CreatePinResponse()

    @Serializable
    @SerialName("SUCCESS")
    data class Success(val description: String) : CreatePinResponse()

    @Serializable
    @SerialName("USER_ID_NOT_FOUND")
    data class UserIdNotFound(val description: String) : CreatePinResponse()

    @Serializable
    @SerialName("INVALID_BASIC_AUTH_CREDENTIALS")
    data class InvalidBasicAuthCredentials(val description: String) : CreatePinResponse()

    @Serializable
    @SerialName("PAY_PIN_IS_REQUIRED")
    data class PayPinRequired(val description: String) : CreatePinResponse()

    @Deprecated(message = "PIN reused allowed in cloud service. Use 'Success' instead", replaceWith = ReplaceWith("Success"), level = DeprecationLevel.WARNING)
    @Serializable
    @SerialName("PIN_REUSED")
    data class PinReused(val description: String) : CreatePinResponse()

    @Serializable
    @SerialName("FAILURE")
    data class Failure(val description: String) : CreatePinResponse()

    @Serializable
    data class Unauthorized(val message: String): CreatePinResponse()

    data class Error(val throwable: Throwable) : CreatePinResponse()

    data class Timeout(val throwable: Throwable) : CreatePinResponse()

    fun <R> fold (
        onBadPayload: (BadPayload: BadPayload) -> R,
        onSuccess: (Success: Success) -> R,
        onUserIdNotFound: (UserIdNotFound: UserIdNotFound) -> R,
        onInvalidBasicAuthCredentials: (InvalidBasicAuthCredentials: InvalidBasicAuthCredentials) -> R,
        onPayPinRequired: (PayPinRequired: PayPinRequired) -> R,
        onPinReused: (PinReused: PinReused) -> R,
        onFailure: (Failure: Failure) -> R,
        onUnauthorized: (Unauthorized: Unauthorized) -> R,
        onError: (Error: Error) -> R,
        onTimeout: (Timeout: Timeout) -> R
    ) : R {
        return when (this) {
            is BadPayload -> onBadPayload(BadPayload())
            is Success -> onSuccess(Success(this.description))
            is UserIdNotFound -> onUserIdNotFound(UserIdNotFound(this.description))
            is InvalidBasicAuthCredentials -> onInvalidBasicAuthCredentials(InvalidBasicAuthCredentials(this.description))
            is PayPinRequired -> onPayPinRequired(PayPinRequired(this.description))
            is PinReused -> onPinReused(PinReused(this.description))
            is Failure -> onFailure(Failure(this.description))
            is Unauthorized -> onUnauthorized(Unauthorized(this.message))
            is Error -> onError(Error(this.throwable))
            is Timeout -> onTimeout(Timeout(this.throwable))
        }
    }
}

@JsExport
sealed class ValidatePinResponse {
    @Serializable
    class BadPayload : ValidatePinResponse()

    @Serializable
    @SerialName("VALID_PIN")
    data class ValidPin(val description: String) : ValidatePinResponse()

    @Serializable
    @SerialName("USER_ID_NOT_FOUND")
    data class UserIdNotFound(val description: String) : ValidatePinResponse()

    @Serializable
    @SerialName("FLOW_ID_NOT_FOUND")
    data class FlowIdNotFound(val description: String) : ValidatePinResponse()

    @Serializable
    @SerialName("INVALID_PIN")
    data class InvalidPin(
        val description: String,
        val attemptsFailed: Int,
        val attemptsRemaining: Int
    ) : ValidatePinResponse()

    @Serializable
    @SerialName("PIN_LOCKED")
    data class PinLocked(
        val description: String,
        val attemptsFailed: Int,
        val attemptsRemaining: Int
    ) : ValidatePinResponse()

    @Serializable
    @SerialName("PIN_NOT_FOUND")
    data class PinNotFound(val description: String) : ValidatePinResponse()

    @Serializable
    @SerialName("TOO_MANY_REQUESTS")
    class TooManyRequests(val description: String) : ValidatePinResponse()

    @Serializable
    @SerialName("FAILURE")
    data class Failure(val description: String) : ValidatePinResponse()

    @Serializable
    @SerialName("PAY_PIN_IS_REQUIRED")
    data class PayPinRequired(val description: String) : ValidatePinResponse()

    @Serializable
    data class Unauthorized(val message: String): ValidatePinResponse()

    data class Error(val throwable: Throwable) : ValidatePinResponse()

    data class Timeout(val throwable: Throwable) : ValidatePinResponse()

    fun <R> fold (
        onBadPayload: (BadPayload: BadPayload) -> R,
        onValidPin: (ValidPin: ValidPin) -> R,
        onUserIdNotFound: (UserIdNotFound: UserIdNotFound) -> R,
        onFlowIdNotFound: (FlowIdNotFound: FlowIdNotFound) -> R,
        onInvalidPin: (InvalidPin: InvalidPin) -> R,
        onPinLocked: (PinLocked: PinLocked) -> R,
        onPinNotFound: (PinNotFound: PinNotFound) -> R,
        onTooManyRequests: (TooManyRequests: TooManyRequests) -> R,
        onPayPinRequired: (PayPinRequired: PayPinRequired) -> R,
        onFailure: (Failure: Failure) -> R,
        onUnauthorized: (Unauthorized: Unauthorized) -> R,
        onError: (Error: Error) -> R,
        onTimeout: (Timeout: Timeout) -> R
    ) : R {
        return when (this) {
            is BadPayload -> onBadPayload(BadPayload())
            is ValidPin -> onValidPin(ValidPin(this.description))
            is UserIdNotFound -> onUserIdNotFound(UserIdNotFound(this.description))
            is FlowIdNotFound -> onFlowIdNotFound(FlowIdNotFound(this.description))
            is InvalidPin -> onInvalidPin(InvalidPin(this.description, this.attemptsFailed, this.attemptsRemaining))
            is PinLocked -> onPinLocked(PinLocked(this.description, this.attemptsFailed, this.attemptsRemaining))
            is PinNotFound -> onPinNotFound(PinNotFound(this.description))
            is TooManyRequests -> onTooManyRequests(TooManyRequests(this.description))
            is PayPinRequired -> onPayPinRequired(PayPinRequired(this.description))
            is Failure -> onFailure(Failure(this.description))
            is Unauthorized -> onUnauthorized(Unauthorized(this.message))
            is Error -> onError(Error(this.throwable))
            is Timeout -> onTimeout(Timeout(this.throwable))
        }
    }
}

@JsExport
sealed class UpdatePinResponse {
    @Serializable
    class BadPayload : UpdatePinResponse()

    @Serializable
    @SerialName("SUCCESS")
    class Success : UpdatePinResponse()

    @Serializable
    @SerialName("FAILURE")
    data class Failure(val description: String) : UpdatePinResponse()

    @Serializable
    @SerialName("PAY_PIN_IS_REQUIRED")
    data class PayPinRequired(val description: String) : UpdatePinResponse()

    @Serializable
    @SerialName("USER_ID_NOT_FOUND")
    data class UserIdNotFound(val description: String) : UpdatePinResponse()

    @Serializable
    @SerialName("INVALID_BASIC_AUTH_CREDENTIALS")
    data class InvalidBasicAuthCredentials(val description: String) : UpdatePinResponse()

    @Serializable
    @SerialName("PIN_REUSED")
    data class PinReused(val description: String) : UpdatePinResponse()

    @Serializable
    data class Unauthorized(val message: String): UpdatePinResponse()

    data class Error(val throwable: Throwable) : UpdatePinResponse()

    data class Timeout(val throwable: Throwable) : UpdatePinResponse()

    fun <R> fold (
        onBadPayload: (BadPayload: BadPayload) -> R,
        onSuccess: (Success: Success) -> R,
         onUserIdNotFound: (UserIdNotFound: UserIdNotFound) -> R,
        onInvalidBasicAuthCredentials: (InvalidBasicAuthCredentials: InvalidBasicAuthCredentials) -> R,
        onPayPinRequired: (PayPinRequired: PayPinRequired) -> R,
        onPinReused: (PinReused: PinReused) -> R,
        onFailure: (Failure: Failure) -> R,
        onUnauthorized: (Unauthorized: Unauthorized) -> R,
        onError: (Error: Error) -> R,
        onTimeout: (Timeout: Timeout) -> R
    ) : R {
        return when (this) {
            is BadPayload -> onBadPayload(BadPayload())
            is Success -> onSuccess(Success())
            is UserIdNotFound -> onUserIdNotFound(UserIdNotFound(this.description))
            is InvalidBasicAuthCredentials -> onInvalidBasicAuthCredentials(InvalidBasicAuthCredentials(this.description))
            is PayPinRequired -> onPayPinRequired(PayPinRequired(this.description))
            is PinReused -> onPinReused(PinReused(this.description))
            is Failure -> onFailure(Failure(this.description))
            is Unauthorized -> onUnauthorized(Unauthorized(this.message))
            is Error -> onError(Error(this.throwable))
            is Timeout -> onTimeout(Timeout(this.throwable))
        }
    }

}

@JsExport
sealed class DeletePinResponse {
    @Serializable
    class BadPayload : DeletePinResponse()

    @Serializable
    @SerialName("SUCCESS")
    class Success : DeletePinResponse()

    @Serializable
    @SerialName("USER_ID_NOT_FOUND")
    data class UserIdNotFound(val description: String) : DeletePinResponse()

    @Serializable
    @SerialName("INVALID_BASIC_AUTH_CREDENTIALS")
    data class InvalidBasicAuthCredentials(val description: String) : DeletePinResponse()

    @Serializable
    @SerialName("PIN_NOT_FOUND")
    data class PinNotFound(val description: String) : DeletePinResponse()

    @Serializable
    @SerialName("FAILURE")
    data class Failure(val description: String) : DeletePinResponse()

    @Serializable
    data class Unauthorized(val message: String): DeletePinResponse()

    data class Error(val throwable: Throwable) : DeletePinResponse()

    data class Timeout(val throwable: Throwable) : DeletePinResponse()

    fun <R> fold (
        onBadPayload: (BadPayload: BadPayload) -> R,
        onSuccess: (Success: Success) -> R,
        onUserIdNotFound: (UserIdNotFound: UserIdNotFound) -> R,
        onInvalidBasicAuthCredentials: (InvalidBasicAuthCredentials: InvalidBasicAuthCredentials) -> R,
        onPinNotFound: (PinNotFound: PinNotFound) -> R,
        onFailure: (Failure: Failure) -> R,
        onUnauthorized: (Unauthorized: Unauthorized) -> R,
        onError: (Error: Error) -> R,
        onTimeout: (Timeout: Timeout) -> R
    ) : R {
        return when (this) {
            is BadPayload -> onBadPayload(BadPayload())
            is Success -> onSuccess(Success())
           is UserIdNotFound -> onUserIdNotFound(UserIdNotFound(this.description))
            is InvalidBasicAuthCredentials -> onInvalidBasicAuthCredentials(InvalidBasicAuthCredentials(this.description))
            is PinNotFound -> onPinNotFound(PinNotFound(this.description))
            is Failure -> onFailure(Failure(this.description))
            is Unauthorized -> onUnauthorized(Unauthorized(this.message))
            is Error -> onError(Error(this.throwable))
            is Timeout -> onTimeout(Timeout(this.throwable))
        }
    }
}